import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const InquiryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [status, setStatus] = useState('processing');
  const [adminReply, setAdminReply] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadInquiry = async () => {
      setLoading(true);
      setError(null);
      setMessage(null);

      const { data, error: inquiryError } = await supabase
        .from('inquiries')
        .select('*, users(name, email)')
        .eq('id', id)
        .single();

      if (cancelled) return;

      if (inquiryError || !data) {
        setError(inquiryError?.message || '문의를 불러오지 못했습니다.');
        setLoading(false);
        return;
      }

      setInquiry(data);
      setStatus(data.status || 'processing');
      setAdminReply(typeof data.admin_reply === 'string' ? data.admin_reply : '');
      setLoading(false);
    };

    loadInquiry();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const reloadInquiry = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);

    const { data, error: inquiryError } = await supabase
      .from('inquiries')
      .select('*, users(name, email)')
      .eq('id', id)
      .single();

    if (inquiryError || !data) {
      setError(inquiryError?.message || '문의를 불러오지 못했습니다.');
      setLoading(false);
      return;
    }

    setInquiry(data);
    setStatus(data.status || 'processing');
    setAdminReply(typeof data.admin_reply === 'string' ? data.admin_reply : '');
    setLoading(false);
  };

  const hasExistingReply = Boolean(inquiry?.admin_reply?.trim());

  const handleSave = async () => {
    if (!inquiry) return;

    const replyText = adminReply.trim();
    if (!replyText) {
      setError('답변 내용을 입력해주세요.');
      return;
    }

    setSaving(true);
    setError(null);
    setMessage(null);

    const nextStatus = status === 'processing' ? 'completed' : status;
    const payload = {
      status: nextStatus,
      admin_reply: replyText,
      replied_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { error: updateError } = await supabase
      .from('inquiries')
      .update(payload)
      .eq('id', inquiry.id);

    setSaving(false);

    if (updateError) {
      setError(updateError.message || '저장에 실패했습니다.');
      return;
    }

    setMessage(hasExistingReply ? '답변이 수정되었습니다.' : '답변이 저장되었습니다.');
    await reloadInquiry();
  };

  const handleDeleteReply = async () => {
    if (!inquiry) return;
    if (!hasExistingReply) {
      setError('삭제할 답변이 없습니다.');
      return;
    }
    if (!window.confirm('관리자 답변을 삭제하시겠습니까? 문의 상태는 처리중으로 변경됩니다.')) {
      return;
    }

    setDeleting(true);
    setError(null);
    setMessage(null);

    const { error: updateError } = await supabase
      .from('inquiries')
      .update({
        admin_reply: null,
        replied_at: null,
        status: 'processing',
        updated_at: new Date().toISOString(),
      })
      .eq('id', inquiry.id);

    setDeleting(false);

    if (updateError) {
      setError(updateError.message || '답변 삭제에 실패했습니다.');
      return;
    }

    setAdminReply('');
    setStatus('processing');
    setMessage('답변이 삭제되었습니다.');
    await reloadInquiry();
  };

  if (loading) {
    return <div>로딩 중...</div>;
  }

  if (error && !inquiry) {
    return (
      <div className="card shadow mb-4">
        <div className="card-body">
          <div className="alert alert-danger mb-3">{error}</div>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/inquiries')}>
            목록으로
          </button>
        </div>
      </div>
    );
  }

  if (!inquiry) {
    return (
      <div className="card shadow mb-4">
        <div className="card-body">
          <div className="alert alert-warning mb-0">문의 정보를 찾을 수 없습니다.</div>
        </div>
      </div>
    );
  }

  const authorLabel = inquiry.users?.email || inquiry.users?.name || '알 수 없음';

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">문의 상세 / 답변</h6>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/inquiries')}>
          목록으로
        </button>
      </div>

      <div className="card-body">
        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="mb-3">
          <h5 className="font-weight-bold mb-1">
            {inquiry.title}{' '}
            <span className={status === 'completed' ? 'badge badge-success' : 'badge badge-warning'}>
              {status === 'completed' ? '답변 완료' : '처리중'}
            </span>
          </h5>
          <div className="text-muted small">
            작성자(아이디): {authorLabel}
            {inquiry.users?.name ? ` (${inquiry.users.name})` : ''}
            {' | '}
            작성일시: {new Date(inquiry.created_at).toLocaleString()}
          </div>
        </div>

        <div className="form-group">
          <label className="font-weight-bold">문의 내용</label>
          <pre
            className="border rounded p-3 mb-0"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', backgroundColor: '#f8f9fc' }}
          >
            {inquiry.content}
          </pre>
        </div>

        <hr />

        <div className="form-group">
          <label className="font-weight-bold d-block">처리상태</label>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="status"
              id="statusProcessingDetail"
              checked={status === 'processing'}
              onChange={() => setStatus('processing')}
            />
            <label className="form-check-label" htmlFor="statusProcessingDetail">
              처리중
            </label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="status"
              id="statusCompletedDetail"
              checked={status === 'completed'}
              onChange={() => setStatus('completed')}
            />
            <label className="form-check-label" htmlFor="statusCompletedDetail">
              답변 완료
            </label>
          </div>
        </div>

        <div className="form-group">
          <label className="font-weight-bold">관리자 답변</label>
          <textarea
            className="form-control"
            rows={7}
            value={adminReply}
            onChange={(e) => setAdminReply(e.target.value)}
            placeholder="사용자에게 보여줄 답변을 입력하세요."
          />
          {inquiry.replied_at && (
            <small className="form-text text-muted">
              최근 답변일시: {new Date(inquiry.replied_at).toLocaleString()}
            </small>
          )}
        </div>

        <div className="d-flex flex-wrap">
          <button type="button" className="btn btn-primary mr-2 mb-2" onClick={handleSave} disabled={saving || deleting}>
            {saving ? '저장 중...' : hasExistingReply ? '답변 수정' : '답변 저장'}
          </button>
          <button
            type="button"
            className="btn btn-outline-danger mr-2 mb-2"
            onClick={handleDeleteReply}
            disabled={saving || deleting || !hasExistingReply}
          >
            {deleting ? '삭제 중...' : '답변 삭제'}
          </button>
          <button type="button" className="btn btn-outline-secondary mb-2" onClick={() => navigate('/inquiries')}>
            목록
          </button>
        </div>
      </div>
    </div>
  );
};

export default InquiryDetail;
